import React, { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export default function Hero() {
  const heroRef = useRef<HTMLElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const subtitleRef = useRef<HTMLParagraphElement>(null)
  const ctaRef = useRef<HTMLAnchorElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    if (!heroRef.current) return

    const ctx = gsap.context(() => {
      // Title animation
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 60, filter: 'blur(12px)' },
        {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: 1.4,
          ease: 'power4.out',
          delay: 0.3,
        }
      )

      // Subtitle
      gsap.fromTo(
        subtitleRef.current,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power3.out',
          delay: 0.8,
        }
      )

      // CTA buttons
      gsap.fromTo(
        ctaRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'power3.out',
          delay: 1.2,
        }
      )

      // Background image parallax
      gsap.fromTo(
        imageRef.current,
        { scale: 1.1, opacity: 0.6 },
        {
          scale: 1,
          opacity: 1,
          duration: 2,
          ease: 'power2.out',
        }
      )

      // Floating particles
      const particles = heroRef.current.querySelectorAll('.hero__particle')
      particles.forEach((p, i) => {
        gsap.to(p, {
          y: -30,
          duration: 3 + (i % 3),
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          delay: i * 0.3,
        })
      })
    }, heroRef.current)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={heroRef} className="hero" id="top">
      <div className="hero__bg">
        <img
          ref={imageRef}
          src="https://images.unsplash.com/photo-1560066984-138dadb4c035?q=80&w=1600&auto=format&fit=crop"
          alt="Inside the Aure Studio salon"
          className="hero__image"
        />
        <div className="hero__overlay" />
        <div className="hero__vignette" />
      </div>

      <div className="hero__content">
        <div className="hero__badge">
          <span className="hero__badge-dot" />
          The People Groomers · Kerala, India
        </div>

        <h1 ref={titleRef} className="hero__title">
          The Art of <em>Personal Grooming</em>
        </h1>

        <p ref={subtitleRef} className="hero__subtitle">
          Beautiful results. Personalised care. An atelier where hair, makeup and skin
          are treated as haute craft.
        </p>

        <div ref={ctaRef} className="hero__cta">
          <a href="#reservations" className="hero__btn hero__btn--primary">
            Book an Appointment
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </a>
          <a href="#services" className="hero__btn hero__btn--ghost">
            Discover the Services
          </a>
        </div>

        <div className="hero__particles">
          <span className="hero__particle" />
          <span className="hero__particle" />
          <span className="hero__particle" />
          <span className="hero__particle" />
          <span className="hero__particle" />
        </div>
      </div>

      <div className="hero__scroll">
        <span>Scroll</span>
        <div className="hero__scroll-line" />
      </div>
    </section>
  )
}